import { motion } from "framer-motion";
import { PARTNERS, BANKS } from "@/lib/data";
import { Award, ShieldCheck, BadgeCheck, Landmark } from "lucide-react";
import { slideInLeftVariant, fadeUpVariant, VIEWPORT_ONCE, SMOOTH_EASING } from "@/lib/animations";

export default function TrustStrip() {
  const bankLogoMap = {
    SBI: "sbi.png",
    PNB: "pnb.png",
    "Bank of Baroda": "baroda.png",
    "Canara Bank": "canara.png",
    HDFC: "hdfc.png",
    ICICI: "icici.png",
  }

  const logoPath = (bank) => {
    const file = bankLogoMap[bank];
    return file ? `/BankLogos/${file}` : null;
  };

  return (
    <section className="relative py-14 bg-white border-y border-slate-100" data-testid="trust-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VIEWPORT_ONCE}
            variants={slideInLeftVariant}
            className="lg:w-1/3"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F26A21] mb-2">Premium Quality. Trusted Brands.</p>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              Powered by India's most trusted solar manufacturers
            </h3>
          </motion.div>

          {/* Marquee */}
          <div className="lg:w-2/3 overflow-hidden relative">
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />
            <div className="flex gap-12 marquee-track w-max">
              {[...PARTNERS, ...PARTNERS].map((p, i) => (
                <div key={`${p.name}-${i}`} className="flex-shrink-0 grid place-items-center w-[140px]">
                  {p.logo ? (
                    <div className="h-24 w-full flex items-center justify-center">
                      <img
                        src={p.logo}
                        alt={p.name}
                        className="max-h-16 max-w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  ) : (
                    <div className="font-display font-extrabold text-2xl text-slate-400 hover:text-[#1B3A8C] transition-colors tracking-tight">
                      {p.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom badge row */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: BadgeCheck, label: "MNRE Empanelled" },
            { icon: ShieldCheck, label: "TSECL Approved Vendor" },
            { icon: Award, label: "PM Surya Ghar Vendor" },
            { icon: Landmark, label: "Bank-Approved Financier" },
          ].map(({ icon: I, label }, i) => {
            const highlight = label === "MNRE Empanelled" || label === "TSECL Approved Vendor";
            return (
              <motion.div
                key={label}
                initial="hidden"
                whileInView="show"
                viewport={VIEWPORT_ONCE}
                custom={i}
                variants={fadeUpVariant}
                className={`flex items-center gap-3 p-4 rounded-xl ${
                  highlight ? "bg-gradient-to-r from-orange-50 to-white border border-orange-200" : "bg-slate-50 border border-slate-100"
                }`}
              >
                <I className={`h-5 w-5 ${highlight ? "text-orange-600" : "text-[#2BA84A]"} flex-shrink-0`} />
                <span className={`text-sm font-semibold ${highlight ? "text-slate-800" : "text-slate-700"}`}>{label}</span>
              </motion.div>
            );
          })}
        </div>

        {/* Banks */}
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 justify-center">
          <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Finance Partners:</span>
          {BANKS.map((b) => {
            const src = logoPath(b);
            return (
              <div key={b} className="flex items-center">
                {src ? (
                  <img
                    src={src}
                    alt={b}
                    className="h-16 object-contain mx-2"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
                {/* <span className="text-sm font-bold text-slate-400 hover:text-[#1B3A8C] transition">{b}</span> */}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
